import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { MessageCircle } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { seo } from '../seoConfig';
import LegalLinks from '../components/LegalLinks';

const FAQ = () => {
  useSeo(seo.faq);
  const handleWhatsApp = () => {
    window.open('https://wa.me/905327866635?text=Hello, I have a question about Powerlab services', '_blank');
  };

  const faqs = [
    {
      question: 'What is Powerlab doing?',
      answer: 'First of all Powerlab is a File Service system but also it helps you to find closest dealer to get tune from Powerlab. This platform is for end users and also for tuners who need file service system.'
    },
    {
      question: 'What is Remap, Chip Tuning, Software?',
      answer: 'All have same meaning, Remap comes from Re-Mapping of the file inside of the ECU, Chip-Tuning comes from the chip which is carrying that files, Software is the name of that file. We are not installing any mechanical parts to your car. We are getting power from your car with only software upgrade!'
    },
    {
      question: "Why don't the manufacturers do it?",
      answer: 'Manufacturers generally sell one vehicle that covers various markets, with different tax and emissions regulations. Different climates with extremes of heat, cold. Different fuel qualities. Different operating altitudes and also driver profiles. A manufacturer has to take all of these factors into consideration and make substantial compromises so vehicles operate without problem.'
    },
    {
      question: 'Why Powerlab?',
      answer: 'Are you looking for more power and torque from your car? You are in the right place to have it. Our files are unique for every car. Your car will have a special file depending on your request and your car condition. Turbo/Supercharger/Natural Aspirated - Diesel/Petrol doesn\'t matter for us to have Performance or Economy. Every car which reaches us has a special own file for it. We are not making copy/paste tuning because all cars have different conditions.'
    },
    {
      question: 'What makes Powerlab different?',
      answer: 'Powerlab is a company providing File Service to all over the world. Most of our files are tested with different dynos from us and also dealers all around the world. We offer maximum power for your car whilst maintaining optimum reliability. We always keep your car safe for Boost Pressures, Rich/Lean Fueling, Exhaust Gas Temperatures and Emission Regulations.'
    },
    {
      question: 'How do you ensure quality?',
      answer: 'Most of our software is developed in Dynoflow SF-849 Chassis Dyno rolling roads to ensure accuracy in terms of both power delivery and also to ensure fueling, boost pressures and exhaust gas temperatures (where applicable) are all within tolerance. We pride ourselves on our ability to achieve maximum power whilst maintaining optimum reliability.'
    },
    {
      question: 'Will chip tuning affect my fuel economy?',
      answer: 'Cars can show different results for economy. In many cases, chip tuning can improve fuel efficiency when driving normally, as the engine operates more efficiently. However, if you use the extra power frequently, fuel consumption may increase.'
    },
    {
      question: 'What about emissions (CO2)?',
      answer: 'Cars can show different results for emissions (CO2). Our tuning maintains safe emission levels while optimizing performance, but results may vary based on driving style and vehicle condition.'
    },
    {
      question: 'Is chip tuning safe for my vehicle?',
      answer: 'Yes, when done professionally by Powerlab. We ensure all modifications stay within safe parameters for boost pressures, fueling, exhaust gas temperatures, and emission regulations. Every file is custom-made for your specific vehicle.'
    },
    {
      question: 'How long does the tuning process take?',
      answer: 'The tuning process typically takes 2-4 hours including diagnostics, ECU reading, file modification, writing, and dyno testing. Complex modifications may require additional time.'
    },
    {
      question: 'Can tuning be reversed?',
      answer: 'Yes, absolutely. We always keep a backup of your original ECU file and can restore it at any time if needed.'
    },
    {
      question: 'Will tuning void my warranty?',
      answer: 'Chip tuning may affect your manufacturer warranty. We recommend discussing this with your dealer. However, we provide our own warranty on our tuning work and can reverse the tune if needed.'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/95 backdrop-blur-md z-50 border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <img 
                src="/brand/logo.png"
                alt="Powerlab Works"
                className="h-14 w-auto object-contain"
              />
            </a>
            <nav className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-300 hover:text-[#FF6600] transition-colors">Home</a>
              <a href="/calculator" className="text-gray-300 hover:text-[#FF6600] transition-colors">Power Calculator</a>
              <a href="/faq" className="text-[#FF6600] font-semibold">FAQ</a>
              <a href="/#contact" className="text-gray-300 hover:text-[#FF6600] transition-colors">Contact</a>
            </nav>
            <Button 
              onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=Appointment Request&body=Hello, I would like to book an appointment for chip tuning.'}
              className="bg-[#FF6600] hover:bg-[#E55A00] text-white"
            >
              Book Appointment
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Frequently Asked <span className="text-[#FF6600]">Questions</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to know about Powerlab Works chip tuning services
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="container mx-auto px-4 max-w-4xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`} 
                className="bg-[#1a1a1a] border border-gray-800 rounded-lg px-6 hover:border-[#FF6600] transition-colors"
              >
                <AccordionTrigger className="text-white hover:text-[#FF6600] text-left py-6">
                  <span className="text-lg font-semibold">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-6 text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto bg-gradient-to-r from-[#FF6600]/10 to-[#E55A00]/10 border-[#FF6600]">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Still have questions?
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                Our team is here to help you with any questions about chip tuning
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=Question about Powerlab Services'}
                  size="lg" 
                  className="bg-[#FF6600] hover:bg-[#E55A00] text-white px-8 py-6 text-lg"
                >
                  Email Us
                </Button>
                <Button 
                  onClick={handleWhatsApp}
                  size="lg" 
                  className="bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-6 text-lg"
                >
                  <MessageCircle className="mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-900 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/brand/logo.png"
                alt="Powerlab"
                className="h-14 w-auto object-contain mb-4"
              />
              <p className="text-gray-500 text-sm">Professional chip tuning and ECU remapping since 2012.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-500 text-sm">
                <li><a href="/calculator" className="hover:text-[#FF6600] transition-colors">Power Calculator</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">Chip Tuning</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">ECU Remapping</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">File Service</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-500 text-sm">
                <li><a href="/#about" className="hover:text-[#FF6600] transition-colors">About Us</a></li>
                <li><a href="/#dealers" className="hover:text-[#FF6600] transition-colors">Our Dealers</a></li>
                <li><a href="/faq" className="hover:text-[#FF6600] transition-colors">FAQ</a></li>
                <li><a href="/#contact" className="hover:text-[#FF6600] transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-gray-500 text-sm">
                <li><a href="tel:+905327866635" className="hover:text-[#FF6600] transition-colors">+90 532 786 6635</a></li>
                <li><a href="mailto:info@powerlab.works" className="hover:text-[#FF6600] transition-colors">info@powerlab.works</a></li>
                <li>Tallinn, Estonia</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-900 pt-8 text-center">
            <p className="text-gray-600 text-sm">
              © 2025 Powerlab Works OU. All rights reserved. | As Fast As You Want, As Safe As Original
            </p>
            <LegalLinks lang="en" className="mt-3" />
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button - Right Middle */}
      <a
        href="https://wa.me/905327866635?text=Hello, I have a question about Powerlab services"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed top-1/2 right-6 -translate-y-1/2 bg-[#25D366] hover:bg-[#1DA851] text-white p-4 rounded-full shadow-2xl z-50 transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
};

export default FAQ;